import type { TaskTypeModule, TaskWidthMode } from './module';
import {
  listTaskTypeCapabilities,
  type TaskTypeCapability,
  type TaskTypeValidatorCapability,
} from './generated/taskTypeCapabilities';
import { loadTaskTypeRuntime } from './runtimeLoaders';

export interface TaskTypeDescriptorCapabilities {
  payloadUpload: boolean;
  llmReviewer: boolean;
  llmValidator: boolean;
  /** What the registered validator does. Null when `llmValidator` is false.
   *  `aiDetectionBlocksSubmit` is the only capability that can refuse a
   *  submission, so the admin campaign UI surfaces it read-only. */
  validator: TaskTypeValidatorCapability | null;
  approvedSubmission: boolean;
  customExport: boolean;
}
export interface TaskTypeDescriptor {
  id: string;
  displayName: string;
  shortName: string;
  description: string;
  widthMode: TaskWidthMode;
  active: boolean;
  capabilities: TaskTypeDescriptorCapabilities;
  /** Load only the selected runtime through an explicit lazy import edge. */
  loadRuntime: () => Promise<TaskTypeModule<any, any>>;
}

export interface DescriptorMetadata {
  id: string;
  displayName: string;
  shortName: string;
  description: string;
}

const METADATA: readonly DescriptorMetadata[] = [
  {
    id: 'arxiv-reasoning',
    displayName: 'ArXiv Reasoning',
    shortName: 'ArXiv',
    description:
      'Author a single-turn reasoning task anchored to a recent arXiv (or bioRxiv/ChemRxiv) paper. Provide a verifiable answer with reasoning traces, then run three models and flag those that fail.',
  },
  {
    id: 'browsecomp',
    displayName: 'BrowseComp',
    shortName: 'BrowseComp',
    description:
      'Construct a web-search-only QA task — prompt, sources, fanout, golden trajectory, and proof that Opus 4.6 fails on it.',
  },
  {
    id: 'bunsen',
    displayName: 'Bunsen',
    shortName: 'Bunsen',
    description:
      'Author a biology prompt for a complex drug-discovery workflow, attach data files, and document a failed agent harness run.',
  },
  {
    id: 'clinical-rubrics',
    displayName: 'Clinical Rubrics',
    shortName: 'Clinical Rubric',
    description:
      'Write a grading rubric for a clinical query: essential and additional facts, plus major and minor incorrect claims.',
  },
  {
    id: 'comp-workflows-drug-discovery',
    displayName: 'Drug Discovery Workflows',
    shortName: 'Drug Discovery',
    description:
      'Submit a hermetic drug-discovery benchmark with validated Harbor metadata, passing authoring-skill reports, and trajectories where agents fail at least half the time.',
  },
  {
    id: 'comp-workflows-scientific',
    displayName: 'Scientific Workflows',
    shortName: 'Scientific',
    description:
      'Submit a hermetic scientific benchmark with validated Harbor metadata, authoring-skill reports (failed runs need a written justification), and trajectories where Gemini fails at least once.',
  },
  {
    id: 'computational-workflow',
    displayName: 'Computational Workflow',
    shortName: 'Comp Workflow',
    description:
      'Author an end-to-end computational challenge — prompt, required tool calls, runnable reference solution, and verifiable correctness criteria.',
  },
  {
    id: 'computational-workflow-docker',
    displayName: 'Computational Workflows',
    shortName: 'Docker Workflow',
    description:
      'Author an end-to-end computational workflow as a runnable docker repo. Clone the comp-workflows skeleton, edit the files for your challenge, and upload the folder. AI-authored submissions are not allowed.',
  },
  {
    id: 'feedback-form',
    displayName: 'Feedback form',
    shortName: 'Feedback',
    description: 'A 1–5 rating plus a free-text comment.',
  },
  {
    id: 'finance-agent-conversations',
    displayName: 'Finance agent conversations',
    shortName: 'Finance Agents',
    description: 'Compare three finance agents through independent conversations, expert feedback, revisions, and final deliverables.',
  },
  {
    id: 'finance-build-rubrics',
    displayName: 'Finance model-build rubric',
    shortName: 'Finance Build',
    description:
      'Write a finance prompt and a weighted rubric, run a Gemini 4 Argon agent to build an Excel workbook from scratch, and grade the result.',
  },
  {
    id: 'finance-rubrics',
    displayName: 'Finance spreadsheet-repair rubric',
    shortName: 'Finance Rubric',
    description:
      'Upload a broken Excel workbook and its source documents, write a prompt and rubric, run a Gemini 4 Argon agent to repair it, and grade the outcome.',
  },
  {
    id: 'environment-builder',
    displayName: 'Environment builder',
    shortName: 'Environment Builder',
    description:
      'Build a realistic legal document environment from a matter framing, folder hierarchy, document archetypes, and optional redacted samples.',
  },
  {
    id: 'gdm-rubrics',
    displayName: 'Research rubric',
    shortName: 'Research Rubric',
    description:
      'Author a research-grade rubric for a scientific problem, then grade three frontier models (GPT-5.5, Claude Opus 4.7, Gemini 3.1 Pro) against it.',
  },
  {
    id: 'drug-discovery-rubrics',
    displayName: 'Drug-discovery research rubric',
    shortName: 'Drug Disc. Rubric',
    description:
      'Author a research rubric for a difficult drug-discovery pipeline problem, then grade GPT-5.6 Sol, Claude 5, and Gemini Flash 3.7 against it.',
  },
  {
    id: 'document-rewrites',
    displayName: 'Document Rewrites',
    shortName: 'Doc Rewrites',
    description:
      'Paste a GPT-Sol high-reasoning ChatGPT conversation URL, attach any seed documents you started from, and explain your document choice and any revisions you made.',
  },
  {
    id: 'open-problems-hitl-auto',
    displayName: 'Open Problems — Auto-Run (HiTL)',
    shortName: 'Auto Problem',
    description:
      'Author an open-problem benchmark (math or physics). Paste the QED link, prompt.tex, and verifier; the workbench runs GPT-5.4 at low / medium / high reasoning and pastes each model output automatically. v2 will also run the QED verifier.',
  },
  {
    id: 'open-problems-hitl-math',
    displayName: 'Open Problems — Math (HiTL)',
    shortName: 'Math Problem',
    description:
      'Author a Tier-1 open-problem benchmark in mathematics — QED link, final prompt.tex, final verifier, and the low/medium/high model runs with paired verifier outputs.',
  },
  {
    id: 'open-problems-hitl-physics',
    displayName: 'Open Problems — Physics (HiTL)',
    shortName: 'Physics Problem',
    description:
      'Author a Tier-1 open-problem benchmark in physics — QED link, final prompt.tex, final verifier, and the low/medium/high model runs with paired verifier outputs.',
  },
  {
    id: 'slop-rewrite',
    displayName: 'Slop Rewrite',
    shortName: 'Slop Rewrite',
    description:
      'Download an AI-generated business document, rewrite it to Project Scout standards, and upload the finished .docx. The AI reviewer compares it against the client’s golden rewrite.',
  },
  {
    id: 'synthetic-rl-env',
    displayName: 'Synthetic Task Review',
    shortName: 'Synthetic Review',
    description:
      'Review a scientific computational workflow bundle for scientific authenticity, realistic data, and reproducibility gaps.',
  },
  {
    id: 'workflow-map',
    displayName: 'Scientific workflow map',
    shortName: 'Workflow Map',
    description:
      'Document a scientific workflow end to end — steps, tools, decision criteria, failure modes, and where expert judgment dominates.',
  },
  {
    id: 'legacy',
    displayName: 'Legacy task',
    shortName: 'Legacy',
    description: 'Fallback for tasks that predate the task type system. Read-only.',
  },
];

const METADATA_BY_ID = new Map(METADATA.map((entry) => [entry.id, entry]));

/**
 * Short names are the label in every space-constrained surface — queue claim
 * buttons, per-type progress bars, the export task-type selector. Two types
 * sharing one makes those controls ambiguous with no compile error, so the
 * table is checked at module load.
 *
 * `defineTaskType` cannot do this: runtimes load lazily and one at a time,
 * so no module ever sees its siblings. This table does.
 */
export function assertUniqueShortNames(entries: readonly DescriptorMetadata[]): void {
  const seen = new Map<string, string>();
  const collisions: string[] = [];
  for (const entry of entries) {
    const key = entry.shortName.trim().toLowerCase();
    const owner = seen.get(key);
    if (owner) collisions.push(`"${entry.shortName}" (${owner} and ${entry.id})`);
    else seen.set(key, entry.id);
  }
  if (collisions.length > 0) {
    throw new Error(`Duplicate task type shortName: ${collisions.join(', ')}.`);
  }
}

assertUniqueShortNames(METADATA);

function capabilitiesFor(entry: TaskTypeCapability): TaskTypeDescriptorCapabilities {
  return {
    payloadUpload: entry.payloadUpload,
    llmReviewer: entry.llmReviewer,
    llmValidator: entry.llmValidator,
    validator: entry.validator,
    approvedSubmission: entry.approvedSubmission,
    customExport: entry.customExport,
  };
}

function buildDescriptor(metadata: DescriptorMetadata, capability: TaskTypeCapability): TaskTypeDescriptor {
  return {
    ...metadata,
    widthMode: capability.widthMode,
    active: capability.active,
    capabilities: capabilitiesFor(capability),
    loadRuntime: () => loadTaskTypeRuntime(metadata.id),
  };
}

const DESCRIPTORS = listTaskTypeCapabilities()
  .map((capability) => {
    const metadata = METADATA_BY_ID.get(capability.id);
    if (!metadata) throw new Error(`Missing task type descriptor metadata for ${capability.id}.`);
    return buildDescriptor(metadata, capability);
  });
const DESCRIPTORS_BY_ID = new Map(DESCRIPTORS.map((entry) => [entry.id, entry]));

export function listTaskTypeDescriptors(includeLegacy = false): TaskTypeDescriptor[] {
  return DESCRIPTORS.filter((entry) => includeLegacy || entry.active);
}

export function getTaskTypeDescriptor(id: string | null | undefined): TaskTypeDescriptor {
  if (id && DESCRIPTORS_BY_ID.has(id)) return DESCRIPTORS_BY_ID.get(id)!;
  return DESCRIPTORS_BY_ID.get('legacy')!;
}
